// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState } from "react";

export const COMPACT_VIEWPORT_QUERY = "(max-width: 980px)";

function queryMatches(query: string): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => queryMatches(query));

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQueryList.matches);

    handleChange();
    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

export const FIELD_GRID_NARROW_QUERY = "(max-width: 599px)";
export const FIELD_GRID_MEDIUM_QUERY = "(max-width: 1023px)";

export function useResponsiveFieldColumns(maxColumns: number): 1 | 2 | 3 {
  const isNarrow = useMediaQuery(FIELD_GRID_NARROW_QUERY);
  const isMedium = useMediaQuery(FIELD_GRID_MEDIUM_QUERY);
  const cappedMax = Math.max(1, Math.min(3, maxColumns)) as 1 | 2 | 3;

  if (isNarrow) {
    return 1;
  }
  if (isMedium) {
    return Math.min(2, cappedMax) as 1 | 2;
  }
  return cappedMax;
}
